import React, { useEffect, useState } from "react";
import { Card, Container, Form } from "react-bootstrap";
import { Formik } from "formik";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "../../assets/icons/index";
import InputField from "../../components/form/InputField";
import CustomSelect from "../../components/form/CustomSelect";
import CommonButton from "../../components/commonButton";
import { routes } from "../../routes/pageRoutes";
import BannerDropzone from "../../components/BannerDropzone";
import { useDispatch } from "react-redux";
import { createIntroductionScreen, updateIntroductionScreen } from "../../redux/slice/introductionScreenSlice";
import { addIntroductionScreenSchema } from "../../schema/validationSchemas";
import { useToast } from "../../context/TostContext";
import { createPortal } from "react-dom";
import { FormFeedback, FormLabel } from "../../components/form/utils";
const AddIntroductionScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const location = useLocation();
    const toast = useToast();
    const { item, isEdit, isView } = location.state || {};

    const handleFileSelect = (e, file, setFieldTouched, values, setValues) => {
        e.stopPropagation();

        if (file) {
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));

            setValues({
                ...values,
                image: file,
                hasExistingImage: false,
            });
        } else {
            setSelectedFile(null);
            setPreview(null);

            setValues({
                ...values,
                image: null,
                hasExistingImage: false,
            });
        }

        setFieldTouched("image", true, false);
    };

    const initialValues = {
        title: item?.title || "",
        description: item?.description || "",
        button_text: item?.button_text || "",
        platform: item?.platform || "all",
        screen_order: item?.screen_order || 0,
        skip_enabled: item?.skip_enabled ?? true,
        cta_enabled: item?.cta_enabled ?? false,
        cta_text: item?.cta_text || "",
        cta_action: item?.cta_action || "register",
        is_active: item?.is_active ?? true,
        image: null,
        hasExistingImage: Boolean(isEdit && item?.file?.url),
        isEdit: Boolean(isEdit),
    };

    const platformOptions = [
        { value: "all", label: "All" },
        { value: "android", label: "Android" },
        { value: "ios", label: "iOS" },
        { value: "web", label: "Web" },
    ];

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("description", values.description);
            formData.append("button_text", values.button_text);
            formData.append("platform", values.platform);
            if (isEdit) {
                formData.append("screen_order", values.screen_order);
            }
            formData.append("skip_enabled", values.skip_enabled);
            formData.append("cta_enabled", values.cta_enabled);
            if (values.cta_enabled) {
                formData.append("cta_text", values.cta_text);
                formData.append("cta_action", values.cta_action);
            }
            formData.append("is_active", values.is_active);

            if (selectedFile) {
                formData.append("file", selectedFile);
            }

            let result;
            if (isEdit) {
                result = await dispatch(
                    updateIntroductionScreen({ id: item.id, formData })
                ).unwrap();
            } else {
                result = await dispatch(createIntroductionScreen(formData)).unwrap();
            }

            if (result?.success) {
                toast.success(result.message || "Screen saved successfully!");
                setTimeout(() => navigate(routes.introductionScreens), 600);
            }
        } catch (err) {
            toast.error(err?.message || "Operation failed");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if ((isEdit || isView) && item?.file?.url) {
            setPreview(item.file.url);
            setSelectedFile(null);
        }
    }, [isEdit, isView, item]);

    const headerPortalTarget = document.getElementById("header-portal-target");
    return (<>
        {headerPortalTarget && createPortal(
            <div className="d-flex align-items-center gap-2">
                <Link
                    to={routes.introductionScreens}
                    className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
                >
                    <ArrowLeftIcon size={22} strokeWidth="2.2" />
                </Link>
                <h4 className="fw-semibold mb-0" style={{ color: '#444050', fontSize: '18px' }}>
                    {isView ? "View Intro Screen" : isEdit ? "Update Intro Screen" : "Add Intro Screen"}
                </h4>
            </div>, headerPortalTarget)}
        <Container>


            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={addIntroductionScreenSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    setFieldValue,
                    handleSubmit,
                    isSubmitting,
                    setFieldTouched,
                    setValues,
                }) => (
                    <Form onSubmit={handleSubmit}>
                        <Card className="bg-white border-0 p-4 rounded-2 shadow-sm">
                            <Card.Header className="p-0 fs-lg bg-transparent border-0 mb-3">Screen Details</Card.Header>
                            
                            <div className="grid gap-4">
                                <InputField
                                    className="g-col-6 g-col-xxl-4 mb-2"
                                    label={{ name: "Title" }}
                                    input={{
                                        id: "title",
                                        name: "title",
                                        type: "text",
                                        placeholder: "Enter screen title",
                                        value: values.title,
                                        onChange: handleChange,
                                        autoFocus: !isView,
                                        disabled: isView,
                                    }}
                                    validation={{
                                        isInvalid: touched.title && errors.title,
                                        message: errors.title
                                    }}
                                />

                                {/* Platform */}
                                <CustomSelect
                                    label={{ name: "Platform" }}
                                    name="platform"
                                    placeholder="Select Platform"
                                    value={values.platform}
                                    onChange={(opt) => !isView && setFieldValue("platform", opt ? opt.value : "")}
                                    options={platformOptions}
                                    isDisabled={isView}
                                    validation={{
                                        isInvalid: touched.platform && errors.platform,
                                        message: errors.platform,
                                    }}
                                    className="g-col-6 g-col-xxl-4 mb-2"
                                />

                                {isEdit && (
                                    <InputField
                                        className="g-col-6 g-col-xxl-4 mb-2"
                                        label={{ name: "Display Order" }}
                                        input={{
                                            id: "screen_order",
                                            name: "screen_order",
                                            type: "number",
                                            placeholder: "0",
                                            value: values.screen_order,
                                            onChange: handleChange,
                                            disabled: isView,
                                        }}
                                        validation={{
                                            isInvalid: touched.screen_order && errors.screen_order,
                                            message: errors.screen_order
                                        }}
                                    />
                                )}

                                {/* Description */}
                                <InputField
                                    className="g-col-12 mb-2"
                                    label={{ name: "Description" }}
                                    input={{
                                        as: "textarea",
                                        rows: 3,
                                        id: "description",
                                        name: "description",
                                        placeholder: "Enter short description",
                                        value: values.description,
                                        onChange: handleChange,
                                        disabled: isView,
                                    }}
                                    validation={{
                                        isInvalid: touched.description && errors.description,
                                        message: errors.description
                                    }}
                                />

                                {/* Button Text */}
                                <InputField
                                    className="g-col-6 g-col-xxl-4 mb-2"
                                    label={{ name: "Next Button Text" }}
                                    input={{
                                        id: "button_text",
                                        name: "button_text",
                                        placeholder: "e.g. Next",
                                        value: values.button_text,
                                        onChange: handleChange,
                                        disabled: isView,
                                    }}
                                    validation={{
                                        isInvalid: touched.button_text && errors.button_text,
                                        message: errors.button_text
                                    }}
                                />

                                {/* Skip Enabled */}
                                <div className="g-col-6 g-col-xxl-4 mb-2">
                                    <FormLabel labelName="Skip Button" />
                                    <Form.Check
                                        type="switch"
                                        id="skip_enabled"
                                        name="skip_enabled"
                                        label={values.skip_enabled ? "Enabled" : "Disabled"}
                                        checked={values.skip_enabled}
                                        onChange={handleChange}
                                        disabled={isView}
                                        className="mt-2"
                                    />
                                </div>

                                {/* CTA Enabled */}
                                <div className="g-col-6 g-col-xxl-4 mb-2">
                                    <FormLabel labelName="CTA Button" />
                                    <Form.Check
                                        type="switch"
                                        id="cta_enabled"
                                        name="cta_enabled"
                                        label={values.cta_enabled ? "Enabled" : "Disabled"}
                                        checked={values.cta_enabled}
                                        onChange={handleChange}
                                        disabled={isView}
                                        className="mt-2"
                                    />
                                </div>

                                {values.cta_enabled && (
                                    <>
                                        {/* CTA Text */}
                                        <InputField
                                            className="g-col-6 g-col-xxl-4 mb-2"
                                            label={{ name: "CTA Button Text" }}
                                            input={{
                                                id: "cta_text",
                                                name: "cta_text",
                                                type: "text",
                                                placeholder: "e.g. Get Started",
                                                value: values.cta_text,
                                                onChange: handleChange,
                                                disabled: isView,
                                            }}
                                            validation={{
                                                isInvalid: touched.cta_text && errors.cta_text,
                                                message: errors.cta_text
                                            }}
                                        />

                                        {/* CTA Action */}
                                        <InputField
                                            className="g-col-6 g-col-xxl-4 mb-2"
                                            label={{ name: "CTA Action" }}
                                            input={{
                                                id: "cta_action",
                                                name: "cta_action",
                                                type: "text",
                                                placeholder: "e.g. register or login",
                                                value: values.cta_action,
                                                onChange: handleChange,
                                                disabled: isView,
                                            }}
                                            validation={{
                                                isInvalid: touched.cta_action && errors.cta_action,
                                                message: errors.cta_action
                                            }}
                                        />
                                    </>
                                )}

                                {isEdit && (
                                    <div className="g-col-6 g-col-xxl-4 mb-2">
                                        <FormLabel labelName="Status" />
                                        <div className="d-flex gap-4 status-radio-group">
                                            <Form.Check
                                                type="radio"
                                                label="Active"
                                                name="is_active"
                                                id="active"
                                                checked={values.is_active === true}
                                                disabled={isView}
                                                onChange={() => !isView && setFieldValue("is_active", true)}
                                            />
                                            <Form.Check
                                                type="radio"
                                                label="Inactive"
                                                name="is_active"
                                                id="inactive"
                                                checked={values.is_active === false}
                                                disabled={isView}
                                                onChange={() => !isView && setFieldValue("is_active", false)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Image Upload */}
                                <div className="g-col-12 mb-2">
                                    <FormLabel labelName="Screen Image" />
                                    <BannerDropzone
                                        preview={preview}
                                        selectedFile={selectedFile}
                                        mimetype={item?.file?.mimetype}
                                        onFileSelect={(e, file) =>
                                            !isView && handleFileSelect(
                                                e,
                                                file,
                                                setFieldTouched,
                                                values,
                                                setValues
                                            )
                                        }
                                        disabled={isView}
                                        existingImageMeta={{ name: item?.file?.file_name }}
                                    />
                                    {touched.image && errors.image && (
                                        <FormFeedback message={errors.image} />
                                    )}
                                </div>

                                {/* Buttons */}
                                <div className="g-col-12 d-flex gap-3 justify-content-end">
                                   
                                    <CommonButton
                                        title={isView ? "Back" : "Cancel"}
                                        variant="outline"
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => navigate(routes.introductionScreens)}
                                    />
                                     {!isView && (
                                        <CommonButton
                                            title="Submit"
                                            type="submit"
                                            variant="primary"
                                            disabled={isSubmitting}
                                        />
                                    )}
                                </div>
                            </div>
                        </Card>
                    </Form>
                )}
            </Formik>
        </Container>
    </>);
};

export default AddIntroductionScreen;
